// Overgezet uit naslag/server/imports/api/gameData/players.ts. De leeshelpers krijgen de staat
// in plaats van het spel-id; de helpers die rechtstreeks schreven en de methode
// `serverSelectTask` zijn acties met dezelfde naam.
import { SpelFout } from '../contract.js';
import { getTask } from '../data/taken.js';
import { pasUpdateToe } from '../mongo.js';
import { TaskType } from '../types/tasks.js';
import { gedeeldeKeuzeLoopt, KEUZE_MELDING, keuzeFout, kostenVanKeuze, spoedVoor, } from './kiezen.js';
import { geldigeNaam, selectSharedTasks, setPlayerName, setSpendBudget, vergelijkbareNaam, } from './player.js';
// Leeshelpers. `undefined` als staat mag, zodat een scherm ze kan aanroepen voordat de
// staat binnen is (het origineel gaf dan ook `undefined`).
export const getPlayerData = (staat) => staat?.players.players;
export const getTasksByPlayerId = (staat, userId) => staat?.players.players.find((playerInCollection) => playerInCollection._id === userId);
export const getSharedPlayerData = (staat) => staat?.players;
/** Het spel-id als er een speler met dit id in het spel zit. */
export const validatePlayerId = (staat, userId) => staat?.players.players.some((p) => p._id === userId) ? staat.players._id : undefined;
/**
 * Bij het inloggen: bestaat de naam al, dan speelt deze browser verder als die speler.
 * Anders het id van de eerste speler zonder naam (een vrije plek). Niet in het origineel:
 * de vergelijking let niet op spaties eromheen of hoofdletters.
 */
export const validateUsername = (staat, username) => {
    const players = staat?.players.players;
    const naam = vergelijkbareNaam(username);
    const existingPlayer = naam
        ? players?.find((p) => p.name !== undefined && vergelijkbareNaam(p.name) === naam)?._id
        : undefined;
    const newPlayer = players?.find((p) => p.name === undefined)?._id;
    return {
        usernameExists: !!existingPlayer,
        playerId: existingPlayer || newPlayer,
    };
};
export const getSharedBudget = (staat) => staat?.players.spendBudget;
export const getSharedSelectedTasks = (staat) => staat?.players.sharedSelectedTasks;
// Acties.
/**
 * `createPlayer` werd in het origineel aangeroepen door `createNewPlayer`, maar die
 * methode bestond op de server niet (en `createNewPlayer` werd door geen enkel scherm
 * gebruikt). Inloggen als nieuwe speler ging via `validateUsername` en `setPlayerName`:
 * de naam komt op de eerste vrije plek. Deze actie doet precies dat in één stap en geeft
 * het id van de speler terug.
 */
export const createPlayer = (staat, ctx, username) => {
    const naam = geldigeNaam(username);
    const { usernameExists, playerId } = validateUsername(staat, naam);
    if (usernameExists)
        throw new SpelFout('Deze naam bestaat al!');
    if (!playerId)
        throw new SpelFout('Er is geen vrije plek meer in dit spel');
    setPlayerName(staat, ctx, playerId, naam);
    return playerId;
};
export const selectSharedTaskAssets = (staat, _ctx, tasksSelected) => {
    pasUpdateToe(staat.players, {
        $set: {
            sharedSelectedTasksAssets: tasksSelected,
        },
    });
};
export const incSpendBudget = (staat, _ctx, diff) => {
    pasUpdateToe(staat.players, {
        $inc: {
            spendBudget: diff,
        },
    });
};
/**
 * Een speler kiest een gedeelde taak of zet hem terug: de nieuwe selectie, het uitgegeven
 * budget en bij onderhoud of revisie de gekozen machine.
 *
 * Het origineel blokkeerde per spel 500 ms lang nieuwe aanroepen (`BLOCKING`), omdat
 * gelijktijdige methodes elkaars wijzigingen konden overschrijven. Een Durable Object voert
 * acties één voor één uit, dus dat slot is hier weggelaten.
 *
 * Het origineel nam `newSelectedTasks` en `budgetDiff` van de browser over. Die zijn
 * gebaseerd op wat die browser het laatst zag: klikken twee spelers tegelijk, dan
 * overschrijft de tweede de keuze van de eerste, betaalt niemand de spoedtoeslag voor een
 * onderdeel dat ze allebei missen en kunnen twee kaarten dezelfde monteur krijgen. Daarom
 * volgt hier alles uit de staat op de server plus `_id` en `remove`:
 *
 * - Staat de taak al zoals gevraagd, dan was dit een dubbele klik en verandert er niets.
 * - Kiezen kan alleen terwijl het gezamenlijke kiezen loopt en alleen een ingebrachte taak.
 *   Een klik die na "Speel taken" binnenkomt, schuift zo niet door naar de volgende ronde.
 * - Een monteur, een opleiding of slijtage voor onderhoud die ontbreekt, geeft een
 *   `SpelFout` met dezelfde tekst als op het scherm (`KEUZE_MELDING`).
 * - `spendBudget` is wat de hele keuze kost, met de spoedtoeslagen (`kostenVanKeuze`).
 *
 * Geeft `true` als er voor deze taak een spoedbestelling bijkomt; dan vraagt het scherm of
 * dat mag. De parameters blijven staan zodat de aanroep gelijk is aan het origineel.
 */
export const serverSelectTask = (staat, ctx, _newSelectedTasks, _budgetDiff, _type, remove, _id, asset) => {
    const { players, sharedSelectedTasks: huidig, sharedSelectedTasksAssets } = staat.players;
    const gekozen = huidig.includes(_id);
    if (remove !== gekozen)
        return false;
    if (!remove) {
        if (!gedeeldeKeuzeLoopt(players))
            throw new SpelFout(KEUZE_MELDING.geenKeuze);
        if (!players.some((speler) => speler.sharedTasks.includes(_id))) {
            throw new SpelFout(KEUZE_MELDING.nietIngebracht);
        }
        const fout = keuzeFout(staat.game, huidig, sharedSelectedTasksAssets, _id, asset);
        if (fout)
            throw new SpelFout(fout);
    }
    const spoed = !remove && spoedVoor(staat.game, huidig, _id);
    const nieuw = remove ? huidig.filter((taskId) => taskId !== _id) : [...huidig, _id];
    selectSharedTasks(staat, ctx, nieuw);
    setSpendBudget(staat, ctx, kostenVanKeuze(staat.game, nieuw));
    const type = getTask(_id)?.type;
    if (type === TaskType.onderhoud || type === TaskType.revisie) {
        if (remove) {
            pasUpdateToe(staat.players, {
                $pull: {
                    sharedSelectedTasksAssets: { id: _id },
                },
            });
        }
        else {
            pasUpdateToe(staat.players, {
                $push: {
                    sharedSelectedTasksAssets: { id: _id, asset },
                },
            });
        }
    }
    return spoed;
};
